"use client";

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType, duration?: number) => void;
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, type: ToastType = "info", duration = 4000) => {
      const id = Math.random().toString(36).substring(2, 9);
      setToasts((prev) => [...prev, { id, message, type, duration }]);
    },
    []
  );

  const success = useCallback((message: string, duration?: number) => addToast(message, "success", duration), [addToast]);
  const error = useCallback((message: string, duration?: number) => addToast(message, "error", duration), [addToast]);
  const warning = useCallback((message: string, duration?: number) => addToast(message, "warning", duration), [addToast]);
  const info = useCallback((message: string, duration?: number) => addToast(message, "info", duration), [addToast]);

  return (
    <ToastContext.Provider value={{ toast: addToast, success, error, warning, info }}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
        <AnimatePresence mode="sync">
          {toasts.map((item) => (
            <ToastCard key={item.id} item={item} onDismiss={() => removeToast(item.id)} />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(100);
  const startTimeRef = useRef(Date.now());
  const remainingTimeRef = useRef(item.duration || 4000);

  useEffect(() => {
    if (isPaused) return;

    const total = item.duration || 4000;
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      const left = Math.max(0, remainingTimeRef.current - elapsed);
      setProgress((left / total) * 100);

      if (left <= 0) {
        clearInterval(interval);
        onDismiss();
      }
    }, 30);

    return () => clearInterval(interval);
  }, [isPaused, item.duration, onDismiss]);

  const handleMouseEnter = () => {
    const elapsed = Date.now() - startTimeRef.current;
    remainingTimeRef.current = Math.max(0, remainingTimeRef.current - elapsed);
    setIsPaused(true);
  };

  const handleMouseLeave = () => {
    startTimeRef.current = Date.now();
    setIsPaused(false);
  };

  const getAccentColor = () => {
    switch (item.type) {
      case "success": return "border-l-[4px] border-l-[#34C759]";
      case "error": return "border-l-[4px] border-l-[#FF453A]";
      case "warning": return "border-l-[4px] border-l-[#FFD60A]";
      default: return "border-l-[4px] border-l-[#FAFAFA]";
    }
  };

  const getIcon = () => {
    switch (item.type) {
      case "success": return <CheckCircle2 className="w-5 h-5 text-[#34C759] shrink-0" />;
      case "error": return <AlertCircle className="w-5 h-5 text-[#FF453A] shrink-0" />;
      case "warning": return <AlertTriangle className="w-5 h-5 text-[#FFD60A] shrink-0" />;
      default: return <Info className="w-5 h-5 text-[#FAFAFA] shrink-0" />;
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, x: 100, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1, y: isPaused ? -2 : 0 }}
      exit={{ opacity: 0, x: 100, height: 0, marginTop: 0, marginBottom: 0, overflow: "hidden" }}
      transition={{ type: "spring", stiffness: 450, damping: 30 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`pointer-events-auto relative overflow-hidden bg-[rgba(28,28,30,0.92)] backdrop-blur-xl border border-[rgba(255,255,255,0.1)] shadow-[0_12px_40px_rgba(0,0,0,0.5)] rounded-xl p-3.5 flex items-start gap-3 ${getAccentColor()}`}
    >
      {getIcon()}
      <div className="flex-1 text-sm font-medium text-[#FAFAFA] leading-tight">
        {item.message}
      </div>
      <button
        onClick={onDismiss}
        className="text-[#8E8E93] hover:text-[#FAFAFA] transition-colors p-1 -mr-1 -mt-1 rounded-md hover:bg-white/10"
      >
        <X className="w-4 h-4" />
      </button>

      {/* Auto-dismiss progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-white/10 overflow-hidden">
        <div
          className="h-full transition-all duration-75 ease-linear"
          style={{
            width: `${progress}%`,
            backgroundColor:
              item.type === "success"
                ? "#34C759"
                : item.type === "error"
                ? "#FF453A"
                : item.type === "warning"
                ? "#FFD60A"
                : "#FAFAFA",
          }}
        />
      </div>
    </motion.div>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
